import type { MinionsPayload, MinionsWeek } from '../report';
import { useMinionsI18n } from '../i18n';
import {
  d1Retention,
  d7Retention,
  emailRate,
  fullWeekFrom,
  inviteConversion,
  inviteLimitRate,
  inviteRate,
  loginMissionRate,
  recurringShare,
  share,
  smsRate,
  stickiness,
  weekPlaysPerPlayer,
  weekTimePerPlayerSec,
} from '../derive';
import { ConsentDonut, WeekBars } from './Charts';
import { Kpi, Stat, Track } from './Parts';

/*
 * The rest of the page in the design's order: engagement and retention, the
 * weekly players and the games-per-player spread, the two missions, marketing
 * consents beside the time per week, and the weekly summary table.
 */

type P = { data: MinionsPayload };

/** "From 10 Nov" while no campaign week is over yet — the figures that need one say when they appear instead of "—". */
function useWaitForWeek1(weeks: MinionsWeek[]) {
  const { t, f } = useMinionsI18n();
  const from = fullWeekFrom(weeks);
  return from == null ? null : t.pendingFrom(f.day(from, 'dayMonth'));
}

export function DauWauCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  const c = data.totals;
  const wait = useWaitForWeek1(data.weeks);
  return (
    <section className="mn-card" id="dauwau">
      <h3 className="mn-h">
        {t.dwTitle}
        <Kpi n="6" />
      </h3>
      <div className="mn-stats">
        <Stat label={t.dwDau} value={f.int(c.dauAvg)} blue />
        <Stat label={t.dwDau7} value={f.int(c.dauLast7)} />
        <Stat label={t.dwWau} value={wait ?? f.int(c.wauLastFullWeek)} pending={wait != null} sub={wait != null && t.pendingSub} />
        <Stat label={t.dwStickiness} value={wait ?? f.pct(stickiness(c))} pending={wait != null} sub={wait != null && t.pendingSub} />
      </div>
      <p className="mn-note">{t.dwNote}</p>
    </section>
  );
}

export function RetentionCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  const c = data.totals;
  const wait = useWaitForWeek1(data.weeks);
  const rows: Array<[string, number | null, 'yellow' | undefined]> = [
    [t.rtD1, d1Retention(c), undefined],
    [t.rtD7, d7Retention(c), undefined],
    [t.rtRecurring, recurringShare(c), 'yellow'],
  ];
  return (
    <section className="mn-card" id="retention">
      <h3 className="mn-h">
        {t.rtTitle}
        <Kpi n="8–9" />
      </h3>
      <div className="mn-hbars">
        {rows.map(([label, pct, tone]) => (
          <div className="mn-hbar" key={label}>
            <span>{label}</span>
            <Track pct={wait ? null : pct} tone={tone} />
            <span className="v">{wait ?? <b>{f.pct(pct)}</b>}</span>
          </div>
        ))}
      </div>
      <p className="mn-note">
        {t.rtNote1}{' '}
        {wait ? (
          t.rtPending
        ) : (
          <>
            <b>{f.int(c.recurringPlayers)}</b> {t.rtNote2}
          </>
        )}
      </p>
    </section>
  );
}

export function WeeklyPlayersCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  return (
    <section className="mn-card" id="weekly-players">
      <h3 className="mn-h">{t.wpTitle}</h3>
      <WeekBars weeks={data.weeks} value={(w) => w.uniquePlayers} fmt={(v) => f.int(v)} label={t.wpTitle} />
      <p className="mn-note">{t.wpNote}</p>
    </section>
  );
}

export function DistributionCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  const total = data.playsDistribution.reduce((a, b) => a + b.players, 0);
  const shares = data.playsDistribution.map((b) => share(b.players, total));
  const max = Math.max(0, ...shares.map((s) => s ?? 0));
  return (
    <section className="mn-card" id="dist">
      <h3 className="mn-h">{t.dsTitle}</h3>
      <div className="mn-hbars">
        {data.playsDistribution.map((b, i) => (
          <div className="mn-hbar" key={b.bucket}>
            <span>
              {b.bucket.replace('-', '–')} {b.max === 1 ? t.game : t.games}
            </span>
            <Track pct={max > 0 && shares[i] != null ? ((shares[i] ?? 0) / max) * 100 : null} />
            <span className="v">
              <b>{f.pct(shares[i])}</b> · {f.int(b.players)}
            </span>
          </div>
        ))}
      </div>
      <p className="mn-note">{t.dsNote}</p>
    </section>
  );
}

export function LoginMissionCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  const c = data.totals;
  const rate = loginMissionRate(c);
  return (
    <section className="mn-card" id="login-mission">
      <h3 className="mn-h">
        {t.lmTitle}
        <Kpi n="10" />
      </h3>
      <div className="mn-hero">
        <span className="mn-big md">{f.pct(rate)}</span>
        <span className="mn-note">{t.lmSub}</span>
      </div>
      <Track pct={rate} tone="yellow" />
      <div className="mn-stats">
        <Stat label={t.lmCompleted} value={f.int(c.loginMission.completed)} />
        <Stat label={t.lmExtra} value={f.int(c.loginMission.extraGames)} />
      </div>
      <p className="mn-note">{t.lmNote}</p>
    </section>
  );
}

export function ReferralCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  const c = data.totals;
  const i = c.invites;
  const cap = data.campaign.inviteCap;
  return (
    <section className="mn-card" id="referral">
      <h3 className="mn-h">
        {t.rfTitle}
        <Kpi n="11–13" />
      </h3>
      <div className="mn-stats">
        <Stat
          label={t.rfRate}
          value={f.pct(inviteRate(c))}
          blue
          sub={
            <>
              <b>{f.int(i.inviters)}</b> {t.rfRateSub}
            </>
          }
        />
        <Stat
          label={t.rfConv}
          value={f.pct(inviteConversion(c))}
          blue
          sub={
            <>
              <b>{f.int(i.inviteesPlayed)}</b> {t.rfConvSub}
            </>
          }
        />
        <Stat label={t.rfSent} value={f.int(i.sent)} />
        <Stat label={t.rfExtra} value={f.int(i.extraGames)} />
      </div>
      <div className="mn-stat" id="invite-limit">
        <span className="mn-stat-l">{t.rfLimit(cap)}</span>
        <div className="mn-hero tight">
          <span className="mn-stat-v blue">{f.int(i.limitReached)}</span>
          <span className="mn-tile-s">
            <b>{f.pct(inviteLimitRate(c))}</b> {t.rfLimitSub}
          </span>
        </div>
      </div>
      <p className="mn-note">{t.rfNote(cap)}</p>
    </section>
  );
}

export function ConsentsCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  const c = data.totals;
  return (
    <section className="mn-card" id="consents">
      <h3 className="mn-h">{t.mcTitle}</h3>
      <div className="mn-hero">
        <span className="mn-big md">{f.int(c.consents.any)}</span>
        <span className="mn-note">{t.mcSub}</span>
      </div>
      <div className="mn-consent">
        <ConsentDonut email={c.consents.email} sms={c.consents.sms} />
        <div className="mn-clegend">
          <div>
            <span>
              <i className="mn-sw blue" /> {t.mcEmail}
            </span>
            <span>{f.int(c.consents.email)}</span>
          </div>
          <div>
            <span>
              <i className="mn-sw yellow" /> {t.mcSms}
            </span>
            <span>{f.int(c.consents.sms)}</span>
          </div>
        </div>
      </div>
      <div className="mn-stats">
        <Stat
          label={t.mcEmailConsents}
          value={f.int(c.consents.email)}
          sub={
            <>
              <b>{f.pct(emailRate(c))}</b> {t.mcOfOptIns}
            </>
          }
        />
        <Stat
          label={t.mcSmsConsents}
          value={f.int(c.consents.sms)}
          sub={
            <>
              <b>{f.pct(smsRate(c))}</b> {t.mcOfOptIns}
            </>
          }
        />
      </div>
    </section>
  );
}

export function TimeCard({ data }: P) {
  const { t, f } = useMinionsI18n();
  return (
    <section className="mn-card" id="time">
      <h3 className="mn-h">{t.tmTitle}</h3>
      <WeekBars weeks={data.weeks} value={(w) => (w.timeSec == null ? null : w.timeSec / 3600)} fmt={(v) => f.hours(v * 3600)} label={t.tmTitle} />
      <p className="mn-note">{t.tmNote}</p>
    </section>
  );
}

/** One row per campaign week; the running week red, weeks to come grey and empty. */
export function WeeklySummary({ data }: P) {
  const { t, f } = useMinionsI18n();
  const tone = (w: MinionsWeek) => (w.status === 'live' ? 'cur' : w.status === 'planned' ? 'future' : undefined);
  return (
    <section className="mn-card" id="summary">
      <h3 className="mn-h">{t.smTitle}</h3>
      <div className="mn-tablebox flat">
        <table aria-label={t.smTitle}>
          <thead>
            <tr>
              <th scope="col">{t.cWeek}</th>
              <th scope="col">{t.cUniquePlayers}</th>
              <th scope="col">{t.cNewPlayers}</th>
              <th scope="col">{t.cPlays}</th>
              <th scope="col">{t.cPpu}</th>
              <th scope="col">{t.cTickets}</th>
              <th scope="col">{t.cLoginMissions}</th>
              <th scope="col">{t.cInvites}</th>
              <th scope="col">{t.cTime}</th>
              <th scope="col">{t.cTpu}</th>
            </tr>
          </thead>
          <tbody>
            {data.weeks.map((w) => (
              <tr key={w.week} className={tone(w)}>
                <td>
                  {t.week(w.week)}
                  {w.status === 'live' ? ` (${t.inProgress})` : ''}
                  <span className="mn-wk-sub">
                    {w.start === w.end ? f.day(w.start, 'dayMonth') : `${f.day(w.start, 'dayMonth')} – ${f.day(w.end, 'dayMonth')}`}
                  </span>
                </td>
                <td>{f.int(w.uniquePlayers)}</td>
                <td>{f.int(w.newPlayers)}</td>
                <td>{f.int(w.plays)}</td>
                <td>{f.dec(weekPlaysPerPlayer(w))}</td>
                <td>{f.int(w.tickets)}</td>
                <td>{f.int(w.loginMissions)}</td>
                <td>{f.int(w.invitesSent)}</td>
                <td>{f.hours(w.timeSec)}</td>
                <td>{f.dur(weekTimePerPlayerSec(w))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mn-note">{t.smNote}</p>
    </section>
  );
}
